import Base from './BaseWithFormatting.js';
import CompanyDetails from '../mixins/CompanyDetails';
import OpeningHours from '../mixins/OpeningHours';
import LoanAmounts from '../mixins/LoanAmounts';
import LoanDuration from '../mixins/LoanDuration';
import Broker from '../mixins/Broker';
import Archived from '../mixins/Archived';
import Interests from '../mixins/Interests';
import Website from '../mixins/Website';
import Trustpilot from '../mixins/Trustpilot.js';
import Compliance from '../mixins/Compliance.js';
import Requirements from '../mixins/Requirements.js';
import SetupFee from '../mixins/SetupFee.js';
import AcceptsPaymentRemarks from '../mixins/AcceptsPaymentRemarks.js';


export default class Loan extends AcceptsPaymentRemarks(SetupFee(Requirements(Compliance(Trustpilot(Website(Interests(Archived(Broker(LoanDuration(LoanAmounts(OpeningHours(CompanyDetails(Base))))))))))))) {

    /*
    |--------------------------------------------------------------------------
    | Compliance
    |--------------------------------------------------------------------------
    */

    get isHighCostCredit() {
        if(this.isFixedInterest) {
            return this._data.interest_fixed > 35;
        }
        return this._data.interest_interval_max > 35;
    }

    /*
    |--------------------------------------------------------------------------
    | Requirements
    |--------------------------------------------------------------------------
    */
    get acceptsNoIncome() {
        return this._data.accepts_no_income;
    }
    
    get acceptsNoSalary() {
        return this._data.accepts_no_salary;
    }

    // spanish
    get acceptsASNEF() {
        return this._data.accepts_asnef;
    }

    /*
    |--------------------------------------------------------------------------
    | First time offer
    |
    | TODO: Implement first time offer
    |--------------------------------------------------------------------------
    */  
    get hasFirstTimeOffer() {
        return this.firstTimeOfferType && this.firstTimeOfferType !== 'false';
    }

    get firstTimeOfferType() {
        return this._data.first_time_offer_type;
    }

    getFirstTimeOfferString(
        templateTo = 'Räntefritt upp till {amount} i {days} dagar för nya kunder', 
        templateFrom = 'Räntefritt från {amount} i {days} dagar för nya kunder', 
    ) {

        if (! this.hasFirstTimeOffer) {
            return '';
        }

        let template = templateFrom;

        if (this.firstTimeOfferType === 'free_to_amount') {
            template = templateTo;
        }


        return template
            .replace('{amount}', this.formatCurrency(this._data.first_time_offer_amount))
            .replace('{days}', this._data.first_time_offer_duration);
    }

    /*
    |--------------------------------------------------------------------------
    | Credit Check
    |--------------------------------------------------------------------------
    */
    get creditCheck() {
        return this._data.credit_check;
    }

    get takesUC() {
        return this.creditCheck == 'uc';
    }

    /*
    |--------------------------------------------------------------------------
    | Features
    |--------------------------------------------------------------------------
    */
    get hasBankId() {
        return this._data.has_bank_id;
    }

    get hasLoanProtection() {
        return this._data.has_loan_protection;
    }

    // denmark
    get hasMitId() {
        return this._data.has_mitid;
    }


    /*
    |--------------------------------------------------------------------------
    | Direct payments
    |--------------------------------------------------------------------------
    */
    get hasDirectPayments() {
        return this._data.has_direct_payments;
    }

    get hasDirectPayments24Hours() {
        return this._data.has_direct_payments_24_hours;
    }

    get hasDirectPaymentToNordea() {
        return this._data.has_direct_payment_to_nordea;
    }

    get hasDirectPaymentToSwedbank() {
        return this._data.has_direct_payment_to_swedbank;
    }

    get hasDirectPaymentToHandelsbanken() {
        return this._data.has_direct_payment_to_handelsbanken;
    }

    get hasDirectPaymentToSEB() {
        return this._data.has_direct_payment_to_seb;
    }

    get hasDirectPaymentToDanskeBank() {
        return this._data.has_direct_payment_to_danske_bank;
    }


    /*
    |--------------------------------------------------------------------------
    | Fees
    |--------------------------------------------------------------------------
    */

    get administrationFee() {
        return this._data.administration_fee;
    }

    get administrationFeeString() {
        return this.formatCurrency(this.administrationFee);
    }

    get extensionFee() {
        return this._data.extension_fee;
    }

    get extensionFeeString() {
        return this.formatCurrency(this.extensionFee);
    }

    get latePaymentFee() {
        return this._data.late_payment_fee;
    }

    get latePaymentFeeString() {
        return this.formatCurrency(this.latePaymentFee);
    }

    get reminderFee() {
        return this._data.reminder_fee;
    }

    get reminderFeeString() {
        return this.formatCurrency(this.reminderFee);
    }
    
    get penaltyInterest() {
        return this._data.penalty_interest;
    }

    get penaltyInterestString() {
        return this.formatPercentage(this.penaltyInterest, 2); 
    }

    /*
    |--------------------------------------------------------------------------
    | Estonian Fees
    |--------------------------------------------------------------------------
    */
    get estonianSetupFeeType() {
        return this._data.setup_fee_type;
    }

    getEstonianSetupFeeString() {
        if (this.estonianSetupFeeType === 'free') {

            return this.formatCurrency(0);
        }

        if (this.estonianSetupFeeType === 'fixed_sum') {
            return this.formatCurrency(this._data.setup_fee_value);
        }

        if (this.estonianSetupFeeType === 'fixed_percentage') {
            return this.formatPercentage(this._data.setup_fee_percentage);
        }

        if (this.estonianSetupFeeType === 'percentage_and_min') {
            return `${this.formatPercentage(this._data.setup_fee_percentage)} (min. ${this.formatCurrency(this._data.setup_fee_value)})`;
        }

        if (this.estonianSetupFeeType === 'percentage_and_fixed') {
            return `${this.formatPercentage(this._data.setup_fee_percentage)} + ${this.formatCurrency(this._data.setup_fee_value)}`;
        }
    }
}